import { localVecToCanvas } from '@/lib/transforms/geometry'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import type { OrientedRect } from '../types/oriented-rect'

/**
 * The point on the box that stays fixed for a given resize `direction`, expressed as an
 * offset from the box's own center in its local (unrotated) frame. Corner handles anchor
 * the opposite corner; edge handles anchor the midpoint of the opposite edge (which is
 * equivalent to "the opposite edge" when that axis isn't resizing at all). This same
 * formula holds for both free and aspect-locked resize — see `computeResizeJS` and
 * `computeResizeConstrainedAspectJS` in `bounding-box-boundary.ts`.
 */
export function resizeAnchorLocalOffset(
  direction: UnifiedSelectionResizeHandle,
  width: number,
  height: number
): { x: number; y: number } {
  return {
    x: direction.includes('w') ? width / 2 : direction.includes('e') ? -width / 2 : 0,
    y: direction.includes('n') ? height / 2 : direction.includes('s') ? -height / 2 : 0,
  }
}

/**
 * Re-derives `x`/`y` for a resized box at a new `width`/`height`, keeping the resize handle's
 * anchor point (opposite corner/edge, per `direction`) exactly where it was in `initial`. Shared
 * by `clampOrientedResizeToMaxSize` and by callers that have already computed their own exact
 * target size (e.g. group-resize descendant capping) and just need the anchor preserved.
 */
export function reanchorResize(
  initial: OrientedRect,
  direction: UnifiedSelectionResizeHandle,
  width: number,
  height: number
): { x: number; y: number; width: number; height: number } {
  const rot = initial.rotation ?? 0
  const w0 = initial.width ?? 0
  const h0 = initial.height ?? 0
  const initialCenter = { x: initial.x + w0 / 2, y: initial.y + h0 / 2 }
  const anchorOffset = localVecToCanvas(resizeAnchorLocalOffset(direction, w0, h0), rot)
  const anchorCanvas = { x: initialCenter.x + anchorOffset.x, y: initialCenter.y + anchorOffset.y }
  const newAnchorOffset = localVecToCanvas(resizeAnchorLocalOffset(direction, width, height), rot)
  const newCenter = {
    x: anchorCanvas.x - newAnchorOffset.x,
    y: anchorCanvas.y - newAnchorOffset.y,
  }

  return {
    x: newCenter.x - width / 2,
    y: newCenter.y - height / 2,
    width,
    height,
  }
}

/**
 * Uniformly scales `next`'s width/height by `scale` (expected ≤1, a shrink), re-anchoring via
 * `reanchorResize`. For callers (e.g. group-resize descendant capping) that have already
 * computed the exact corrective scale themselves — bypasses `clampOrientedResizeToMaxSize`'s
 * "already oversized at gesture start" skip, which assumes a fixed external ceiling; here the
 * scale is re-derived fresh every tick from live descendant state, so that skip's rationale
 * doesn't apply and would produce wrong results if reused.
 */
export function applyUniformScaleAboutResizeAnchor(
  initial: OrientedRect,
  direction: UnifiedSelectionResizeHandle,
  next: { x: number; y: number; width: number; height: number },
  scale: number
): { x: number; y: number; width: number; height: number } {
  if (scale === 1) return next

  return reanchorResize(initial, direction, next.width * scale, next.height * scale)
}
